import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { collection, query, where, limit, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import { subscribeAuth, signOut } from "../services/auth";
import { setPaused, quickStockUpdate } from "../services/sellers";
import { SEGMENTS } from "../services/constants";
import { compactNumber } from "../utils/format";
import TierBadge from "../components/TierBadge";
import MatchesInbox from "../components/MatchesInbox";

// Seller dashboard. Editing happens here; the full-fledged wizard is on
// /post for first-time listing creation. This page is the day-to-day
// "update stock, see contacts, edit profile" surface.
export default function Me() {
  const [user, setUser] = useState(null);
  const [seller, setSeller] = useState(null);
  const [buyer, setBuyer] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => subscribeAuth(setUser), []);

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    let cancelled = false;
    (async () => {
      // Look up storefront and buyer profile in parallel — a single PA
      // account can carry either or both (typical whale: seller + buyer
      // in different regions).
      const sellerQ = query(
          collection(db, "pop_market_sellers"),
          where("paUid", "==", user.uid),
          limit(1),
      );
      const buyerQ = query(
          collection(db, "pop_market_buyers"),
          where("paUid", "==", user.uid),
          limit(1),
      );
      const [sSnap, bSnap] = await Promise.all([
        getDocs(sellerQ),
        getDocs(buyerQ).catch(() => ({ docs: [] })),
      ]);
      if (cancelled) return;
      const sDoc = sSnap.docs[0];
      const bDoc = bSnap.docs[0];
      setSeller(sDoc ? { handle: sDoc.id, ...sDoc.data() } : null);
      setBuyer(bDoc ? { handle: bDoc.id, ...bDoc.data() } : null);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [user]);

  if (!user) {
    return (
      <div className="container" style={{ maxWidth: 460, paddingTop: 40, textAlign: "center" }}>
        <p>You need to sign in to manage a storefront.</p>
        <div style={{ display: "flex", gap: 8, justifyContent: "center", flexWrap: "wrap" }}>
          <Link to="/signin" className="auth-btn primary">Sign in</Link>
          <Link to="/signup" className="auth-btn ghost">Create account</Link>
        </div>
      </div>
    );
  }
  if (loading) return <div className="container">Loading…</div>;

  if (!seller && !buyer) {
    return (
      <div className="container" style={{ maxWidth: 520, paddingTop: 32 }}>
        <h1 style={{ marginTop: 0 }}>Welcome 👋</h1>
        <p className="muted">
          You're signed in but don't have a storefront or buyer profile yet.
          Set one up in under 2 minutes.
        </p>
        <Link to="/post" className="auth-btn primary" style={{ marginTop: 12 }}>
          Create my storefront
        </Link>
      </div>
    );
  }

  // Buyer-only user (no storefront) — surface their inbox and a nudge
  // to also list as a seller.
  if (!seller && buyer) {
    return (
      <div className="container" style={{ paddingBottom: 80 }}>
        <header style={{ padding: "8px 0 24px", borderBottom: "1px solid var(--border)", marginBottom: 16 }}>
          <h1 style={{ fontSize: 26, fontWeight: 800, margin: 0 }}>
            {buyer.displayName || buyer.handle}
          </h1>
          <div className="muted" style={{ fontSize: 13 }}>
            <Link to={`/buyer/${buyer.handle}`} target="_blank" rel="noopener noreferrer">
              View public buyer profile →
            </Link>
          </div>
        </header>
        <MatchesInbox handle={buyer.handle} role="buyer" />
      </div>
    );
  }

  return (
    <div className="container" style={{ paddingBottom: 80 }}>
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0 24px", borderBottom: "1px solid var(--border)", marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 26, fontWeight: 800, margin: 0, display: "flex", alignItems: "center", gap: 10 }}>
            {seller.displayName || seller.handle}
            <TierBadge tier={seller.tier} />
          </h1>
          <div className="muted" style={{ fontSize: 13 }}>
            <Link to={`/seller/${seller.handle}`} target="_blank" rel="noopener noreferrer">
              View public storefront →
            </Link>
            <span style={{ marginLeft: 12 }}>
              Status: {seller.paused ? "Paused" : seller.status === "active" ? "Active" : seller.status}
            </span>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={() => setPaused(seller.handle, !seller.paused)}>
            {seller.paused ? "Resume" : "Pause"}
          </button>
          <button onClick={signOut}>Sign out</button>
        </div>
      </header>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 14 }}>
        <StatCard label="Contact clicks (all-time)" value={compactNumber(seller.contactCountTotal || 0)} />
        <StatCard label="Reviews" value={compactNumber(seller.reviewCount || 0)} sub={seller.avgRating ? `★ ${Number(seller.avgRating).toFixed(2)}` : null} />
        <StatCard label="Proven reserves" value={seller.reserveProofTier && seller.reserveProofTier !== "none" ? `≥${seller.reserveProofTier}` : "Unverified"} />
      </div>

      <MatchesInbox handle={seller.handle} role="seller" />
      {buyer && <MatchesInbox handle={buyer.handle} role="buyer" />}

      <h2 style={{ fontSize: 14, fontWeight: 800, letterSpacing: 1, textTransform: "uppercase", color: "var(--text-muted)", marginTop: 32 }}>
        Quick stock update
      </h2>
      <p className="dim" style={{ fontSize: 12, marginTop: -4 }}>
        Refresh these after a farming session or right before a battle close
        so buyers see your real availability.
      </p>
      <StockUpdater seller={seller} onUpdated={(next) => setSeller({ ...seller, ...next })} />

      <h2 style={{ fontSize: 14, fontWeight: 800, letterSpacing: 1, textTransform: "uppercase", color: "var(--text-muted)", marginTop: 32 }}>
        Profile
      </h2>
      <p className="dim" style={{ fontSize: 12, marginTop: -4 }}>
        Full edit form lives at <Link to="/post?edit=1">/post?edit=1</Link>.
      </p>
    </div>
  );
}

function StatCard({ label, value, sub }) {
  return (
    <div className="card">
      <div className="dim" style={{ fontSize: 10, fontWeight: 800, letterSpacing: 1, textTransform: "uppercase" }}>
        {label}
      </div>
      <div style={{ fontSize: 26, fontWeight: 800, marginTop: 4 }}>{value}</div>
      {sub && <div className="muted" style={{ fontSize: 13, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function StockUpdater({ seller, onUpdated }) {
  const [draft, setDraft] = useState(seller.stock || {});
  const [saving, setSaving] = useState(false);
  const dirty = SEGMENTS.some((s) => Number(draft[s.key] || 0) !== Number(seller.stock?.[s.key] || 0));

  const save = async () => {
    setSaving(true);
    try {
      await quickStockUpdate(seller.handle, draft);
      onUpdated({ stock: draft });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card" style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 8 }}>
      {SEGMENTS.map((s) => (
        <div key={s.key} style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span className={`segment-pill ${s.cssClass}`} style={{ minWidth: 100 }}>{s.label}</span>
          <input
            type="number"
            inputMode="numeric"
            value={draft[s.key] || 0}
            onChange={(e) => setDraft({ ...draft, [s.key]: Number(e.target.value) || 0 })}
            style={{ flex: 1, maxWidth: 200 }}
          />
        </div>
      ))}
      <button className="primary" disabled={!dirty || saving} onClick={save}>
        {saving ? "Saving…" : "Update stock"}
      </button>
    </div>
  );
}
